import type { ReactNode } from "react";
import React, { useContext } from "react";
import { buildApiReferencePrefix, DocsContext } from "../chrome/DocsContext";
import type { FullMetaBlob } from "../chrome/FullMetaBlob";
import { ExampleAppMeta } from "../ExampleAppMeta";
import { DocLink } from "./DocLink";

const useMandatoryCurrentApp = (given?: FullMetaBlob): FullMetaBlob => {
  let app = given;
  const { currentApp } = useContext(DocsContext);
  if (!app) {
    if (!currentApp) throw new Error("Can't render an APIReferenceRelativeLink without a current app selected");
    app = currentApp;
  }
  return app;
};

const useExampleFallbackCurrentApp = (): FullMetaBlob => {
  let app = ExampleAppMeta;
  const { currentApp } = useContext(DocsContext);
  if (currentApp) {
    app = currentApp;
  }
  return app;
};

/**
 * Special link component that knows how to link to a path relative to the current application's API reference
 * We can't just use normal [link](url) links in the api reference MDX because the link href is dynamic based on the current app's slug, so instead we use this thing to make that easy.
 * */
export const APIReferenceRelativeLink = (props: { children: ReactNode; path: string; app?: FullMetaBlob }) => {
  const app = useMandatoryCurrentApp(props.app);
  return <DocLink href={`${buildApiReferencePrefix(app)}${props.path}`}>{props.children}</DocLink>;
};

/**
 * Link component which links to the API reference if the current user is logged in an has an app selected, otherwise just renders the children with no link.
 */
export const ExampleFallbackAPIReferenceRelativeLink = (props: { children: ReactNode; path: string }) => {
  const app = useExampleFallbackCurrentApp();
  return <APIReferenceRelativeLink app={app} {...props} />;
};
